import { supabase } from "@/integrations/supabase/client";

/**
 * Calculate user's account age in days
 */
export async function getUserAccountAge(userId: string): Promise<number> {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('created_at')
      .eq('id', userId)
      .single();
    
    if (!profile?.created_at) return 0;
    
    const createdDate = new Date(profile.created_at);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - createdDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating account age:', error);
    return 0;
  }
}

/**
 * Calculate phase progress percentage
 */
export async function calculatePhaseProgress(
  unaId: string,
  phaseNumber: number
): Promise<number> {
  try {
    // Get workflows for this phase
    const { data: workflows } = await supabase
      .from('workflows')
      .select('id')
      .eq('is_active', true)
      .contains('recommended_phases', [phaseNumber]);
    
    if (!workflows || workflows.length === 0) return 0;
    
    const workflowIds = workflows.map(w => w.id);
    
    // Get completed workflows for this UNA
    const { data: completed } = await supabase
      .from('workflow_completion_tracking')
      .select('workflow_id')
      .eq('una_id', unaId)
      .not('completed_at', 'is', null)
      .in('workflow_id', workflowIds);
    
    const completedCount = completed?.length || 0;
    const totalCount = workflows.length;
    
    return Math.round((completedCount / totalCount) * 100);
  } catch (error) {
    console.error('Error calculating phase progress:', error);
    return 0;
  }
}

/**
 * Get user experience level based on account age and activity
 */
export async function getUserExperienceLevel(
  userId: string
): Promise<'new' | 'learning' | 'power'> {
  try {
    const accountAge = await getUserAccountAge(userId);
    
    // Check completed workflows count
    const { data: completed } = await supabase
      .from('workflow_completion_tracking')
      .select('id')
      .eq('user_id', userId)
      .not('completed_at', 'is', null);
    
    const completedCount = completed?.length || 0;
    
    // New users: First 7 days
    if (accountAge < 7) return 'new';
    
    // Power users: 5+ completed workflows OR 30+ days
    if (completedCount >= 5 || accountAge >= 30) return 'power';
    
    // Learning users: Everything in between
    return 'learning';
  } catch (error) {
    console.error('Error determining experience level:', error);
    return 'new';
  }
}

/**
 * Get current journey phase for user
 */
export async function getCurrentJourneyPhase(userId: string): Promise<number> {
  try {
    const { data } = await supabase
      .from('user_journey_state')
      .select('current_phase')
      .eq('user_id', userId)
      .single();
    
    return data?.current_phase || 0;
  } catch (error) {
    console.error('Error getting current phase:', error);
    return 0;
  }
}

/**
 * Check if user should see onboarding overlay
 */
export async function shouldShowOnboarding(userId: string): Promise<boolean> {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('display_preferences')
      .eq('id', userId)
      .single();
    
    const prefs = profile?.display_preferences as any;
    
    // Show if user hasn't dismissed it and is new
    if (prefs?.onboarding_dismissed) return false;
    
    const accountAge = await getUserAccountAge(userId);
    return accountAge === 0; // Only show on first day
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return false;
  }
}

/**
 * Dismiss onboarding overlay
 */
export async function dismissOnboarding(userId: string): Promise<void> {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('display_preferences')
      .eq('id', userId)
      .single();
    
    const prefs = (profile?.display_preferences as any) || {};
    
    await supabase
      .from('profiles')
      .update({
        display_preferences: {
          ...prefs,
          onboarding_dismissed: true,
        },
      })
      .eq('id', userId);
  } catch (error) {
    console.error('Error dismissing onboarding:', error);
  }
}

/**
 * Get user dashboard preferences
 */
export async function getUserDashboardPreferences(
  userId: string
): Promise<any> {
  try {
    const { data } = await supabase
      .from('profiles')
      .select('display_preferences')
      .eq('id', userId)
      .single();
    
    return data?.display_preferences || {};
  } catch (error) {
    console.error('Error getting dashboard preferences:', error);
    return {};
  }
}

/**
 * Update user dashboard preferences
 */
export async function updateDashboardPreferences(
  userId: string,
  preferences: any
): Promise<void> {
  try {
    const current = await getUserDashboardPreferences(userId);
    
    await supabase
      .from('profiles')
      .update({
        display_preferences: {
          ...current,
          ...preferences,
        },
      })
      .eq('id', userId);
  } catch (error) {
    console.error('Error updating dashboard preferences:', error);
  }
}
